import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { MenuSearchIcon } from '@/assets/icons/icons';
import CustomShipmentText from '@/components/CustomShipmentText';
import SearchInput from '@/components/SearchInput';
import type { PanelScreen } from '@/pages/Logiblocks/types';
import { DelayEventCard } from './DelayEventCard';
import {
  DelayEventKind,
  EVENT_KIND_META,
  OFFERED_EVENT_KINDS,
} from './delayUntilEventModel';
import styles from './DelayUntilEvent.module.css';

interface DelayAddEventScreenProps {
  onPick: (kind: DelayEventKind) => void;
  /** Back arrow and Cancel: return to the Delay panel without adding anything. */
  onBack: () => void;
  setPanelScreen?: (screen: PanelScreen | null) => void;
}

/**
 * The "Add event" screen: every event the Delay can wait for, grouped, with a
 * search box. Picking one opens that event's settings.
 */
export const DelayAddEventScreen = ({
  onPick,
  onBack,
  setPanelScreen,
}: DelayAddEventScreenProps) => {
  const [search, setSearch] = useState('');
  const onBackRef = useRef(onBack);
  onBackRef.current = onBack;

  useEffect(() => {
    setPanelScreen?.({
      title: 'Add event',
      backLabel: 'Delay',
      onBack: () => onBackRef.current(),
    });
    return () => setPanelScreen?.(null);
  }, [setPanelScreen]);

  // Only groups with a matching offered event are drawn.
  const groups = useMemo(() => {
    const query = search.trim().toLowerCase();
    const found: { heading: string; kinds: DelayEventKind[] }[] = [];
    for (const kind of OFFERED_EVENT_KINDS) {
      const { label, description, menuGroup } = EVENT_KIND_META[kind];
      if (
        query &&
        !label.toLowerCase().includes(query) &&
        !description.toLowerCase().includes(query)
      ) {
        continue;
      }
      const group = found.find((g) => g.heading === menuGroup);
      if (group) group.kinds.push(kind);
      else found.push({ heading: menuGroup, kinds: [kind] });
    }
    return found;
  }, [search]);

  return (
    <div className={styles.addEventScreen}>
      <div className={styles.addEventSearch}>
        <SearchInput
          placeholder='Search'
          onChange={setSearch}
          width='100%'
          style={{
            padding: '0 8px',
            backgroundColor: 'var(--button-neutral-bg)',
          }}
          prefixIcon={
            <span className={styles.addEventSearchIcon}>
              <MenuSearchIcon width={11.66} height={11.66} />
            </span>
          }
        />
      </div>
      {groups.length === 0 && (
        <div className={styles.addEventEmpty}>No events found</div>
      )}
      {groups.map((group, index) => (
        <Fragment key={group.heading}>
          {index > 0 && <hr className={styles.addEventDivider} />}
          <div className={styles.addEventGroup}>
            <CustomShipmentText
              text={group.heading}
              fontSize='14px'
              lineHeight='16px'
              fontWeight={600}
            />
            {group.kinds.map((kind) => (
              <DelayEventCard
                key={kind}
                kind={kind}
                title={EVENT_KIND_META[kind].label}
                subtitle={EVENT_KIND_META[kind].description}
                onClick={() => onPick(kind)}
              />
            ))}
          </div>
        </Fragment>
      ))}
    </div>
  );
};
